import {AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {NgClass, NgStyle} from '@angular/common';
import {TypingDirective} from '../../directives/typing.directive';
import {FormsModule} from '@angular/forms';
import {InputTextModule} from 'primeng/inputtext';
import {ChatbotIcons, ChatbotMsg, ChatbotRole} from '../../interfaces/library.interface';
import {DataStateService, KlerosAiService, StorageService} from '../../../../../core/service';
import {QuestionRequestModel} from '../../../../models';
import {finalize, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
    selector: 'chatbot-textbox',
	imports: [NgStyle, NgClass, TypingDirective, FormsModule, InputTextModule, TranslatePipe],
    template: `
      <div class="header text-center">
        <div class="flex justify-content-center align-items-center">
          <span class="header-text">{{ titleText }}</span>
					<span class="icon-right" (click)="onCloseChatbot()">
						<i class="pi pi-times"></i>
					</span>
        </div>
        <i class="text-xs">({{ 'infoAssistenteBot'|translate }})</i>
      </div>
      <div #bodyChatbotContainer class="body">
        @for (chatMsg of listOfMessages; track $index) {
          <div class="chat-container-message" [ngStyle]="{'flex-direction': ChatbotRole.BOT === chatMsg.role ? 'row' : 'row-reverse'}">
            <img [src]="ChatbotRole.BOT === chatMsg.role ? icons.chatbotIcon : icons.userIcon" class="svg-container" alt=""
                 [ngClass]="ChatbotRole.BOT === chatMsg.role ? 'chatbot-icon' : 'user-icon'">
            @if (ChatbotRole.BOT === chatMsg.role) {
              <p class="chatbot-text chat-message" [class.chat-error]="chatMsg.error">{{ chatMsg.content }}</p>
            } @else {
              <p libTyping [text]="chatMsg.content" [nativeContainer]="bodyChatbotContainer" class="chatbot-text">
                {{ chatMsg.content }}
              </p>
            }
          </div>
        }
        @if (waitingResponse) {
          <div class="chat-container-message">
            <img [src]="icons.chatbotIcon" class="svg-container chatbot-icon" alt="">
            <div class="spinner">
              <div class="bounce1"></div>
              <div class="bounce2"></div>
              <div class="bounce3"></div>
            </div>
          </div>
        }
      </div>
      <hr class="my-1 mx-2"/>
      <div class="footer">
        <input #inputRef
               type="text" pInputText [placeholder]="textPH"
               [disabled]="waitingResponse"
               [style]="{'font-size' : 'small' , 'width' : '85%'}"
               [(ngModel)]="inputText"
               (keydown.enter)="onSendForm()"/>
        <div class="send-icon-container" [class.disabled-icon]="!inputText" (click)="onSendForm()"><i class="pi pi-send"></i>
        </div>
      </div>
		`,
    styleUrl: 'chatbot-text.component.scss'
})
export class ChatbotTextboxComponent implements OnInit, AfterViewChecked, OnDestroy {

	@ViewChild('bodyChatbotContainer', {static: false}) bodyContainer !: ElementRef;
	@ViewChild('inputRef', {static: false}) inputRef !: ElementRef;

	@Input({required: true}) icons!: ChatbotIcons;
	// labels:
	@Input({required: true}) titleText!: string;
	@Input() textPH = '';
	@Input({required: true}) welcomeMessage: string;
	@Input({required: true}) errorMessage: string;
	@Input({required: true}) chatbotStorageKey: string;

	@Output() closeChatbot = new EventEmitter<ChatbotMsg[]>();

	inputText: string | undefined;
	waitingResponse = false;
	listOfMessages: ChatbotMsg[];

	ChatbotRole = ChatbotRole;

	subscriptions: Subscription = new Subscription();

	constructor(private state: DataStateService,
							private storage: StorageService,
							private klerosAIService: KlerosAiService) {
	}

	ngOnInit() {
		// carico messaggi: se c'è qualcosa nel localstorage prendo da li, altrimenti "vuoto"
		const messagesFromStorage = this.storage.getItemLocal(this.chatbotStorageKey);
		this.listOfMessages = messagesFromStorage ? JSON.parse(messagesFromStorage) : [{role: ChatbotRole.BOT, content: this.welcomeMessage}];
	}

	ngAfterViewChecked() {
		this.inputRef.nativeElement.focus();
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	onCloseChatbot(): void {
		this.closeChatbot.emit(this.listOfMessages);
	}

	onSendForm(): void {
		if (this.inputText) {
			const question = <string>this.inputText;
			this.listOfMessages.push({role: ChatbotRole.USER, content: question});
			this.inputText = undefined;
			this.waitingResponse = true;

			// Remove the welcome message
			const indexWelcomeMessage: number = this.listOfMessages.findIndex(m => m.content === this.welcomeMessage);
			if (indexWelcomeMessage !== -1) {
				this.listOfMessages.splice(indexWelcomeMessage, 1);
			}

			const request: QuestionRequestModel = {baseNode: this.state.getIdRoot(), question: question};

			this.subscriptions.add(this.klerosAIService.question(request)
				.pipe(finalize(() => this.waitingResponse = false), catchError(err => {
					this.listOfMessages.push({role: ChatbotRole.BOT, content: this.errorMessage, error: true});
					throw err;
				})).subscribe(resp => {
					if (resp.answer) {
						this.listOfMessages.push({role: ChatbotRole.BOT, content: resp.answer});
					}
				}));
		}
	}

}
